import React, { useState } from 'react';
import classNames from 'classnames';
import { useDispatch } from 'umi';
import { Bubble, Button, SearchBox, Checkbox } from 'tea-component';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { useDblIntl } from '@/pages/datalab/utils/utils';
import Abnormal from '@/pages/home/components/Abnormal';
import CreateChart from '@/pages/datalab/pages/WorkBench/components/CreateChart';
import RightChartList from '@/pages/datalab/pages/WorkBench/components/RightModal/RightChartList';
import { chartListItemType } from '@/pages/datalab/pages/WorkBench/CreateReport/_type';
import { AI_ANALYSIS_TYPE } from '@dbl/core/dashboard';
import StartsSvg from '@/pages/datalab/assets/svg/starts.svg';

import '../index.scss';
import Loading from '@home/components/Loading';

interface IRightChartTabProps {
  handleDragStart: (item: chartListItemType | null) => void;
  chartListData: chartListItemType[];
  loading: boolean;
  error: boolean;
  hasChartPrivilege: boolean;
  handleOnSubmit: () => void;
  hasMore: boolean;
  loadMore: () => void;
  loadingMore: boolean;
  searchValue: string;
  onSearchChange: (value: string) => void;
  sort: 'asc' | 'desc';
  onSortChange: (sort: 'asc' | 'desc') => void;
  onlyMyCharts: boolean;
  onOnlyMyChartsChange: (value: boolean) => void;
}
const RightChartTab = (props: IRightChartTabProps) => {
  const {
    handleDragStart,
    chartListData,
    handleOnSubmit,
    loading,
    error,
    hasChartPrivilege,
    hasMore,
    loadMore,
    loadingMore,
    searchValue,
    onSearchChange,
    sort,
    onSortChange,
    onlyMyCharts,
    onOnlyMyChartsChange,
  } = props;
  const [showCreateChart, setShowCreateChart] = useState<boolean>(false);
  const { dblFormatMessage, formatMessage } = useDblIntl('create_dashboard');
  const dispatch = useDispatch();
  const isAiAnalysisDisabled = !hasChartPrivilege;

  // 直接使用从接口返回的数据，不再进行前端过滤和排序
  const chartList = chartListData || [];

  const showCreateChartModel = () => {
    setShowCreateChart(true);
    dispatch({
      type: 'customDashboard/save',
      payload: {
        isHideCustomLeaveModal: true,
      }
    });
  };

  return (
    <div className="dbl-charts-module">
      <div
        className={classNames('dbl-ai-analysis-entry', {
          'dbl-ai-analysis-entry--disabled': isAiAnalysisDisabled,
        })}
        draggable={!isAiAnalysisDisabled}
        onDragStart={(event) => {
          if (isAiAnalysisDisabled) {
            return;
          }

          // 拖拽时需要一张“拖拽预览图”(drag image)。
          // 这里按需在内存中临时构建 DOM，并通过 setDragImage 指定给浏览器使用。
          // 为避免在拖拽过程中残留临时节点，这里会在 finally 中移除。
          const dragImageNode = document.createElement('div');
          dragImageNode.style.display = 'flex';
          dragImageNode.style.alignItems = 'center';
          dragImageNode.style.gap = '6px';
          dragImageNode.style.width = '198px';
          dragImageNode.style.height = '44px';
          dragImageNode.style.padding = '10px 16px';
          dragImageNode.style.borderRadius = '4px';
          dragImageNode.style.background = '#ffffff';
          dragImageNode.style.boxShadow = '0 6px 15px 0 rgba(38, 40, 50, 0.16)';
          dragImageNode.style.boxSizing = 'border-box';

          const iconNode = document.createElement('img');
          iconNode.src = StartsSvg;
          iconNode.width = 15;
          iconNode.height = 14;
          iconNode.alt = '';
          iconNode.setAttribute('aria-hidden', 'true');

          const titleNode = document.createElement('span');
          titleNode.innerText = formatMessage({ id: 'dbl_ai_analysis_card_title' });
          titleNode.style.color = '#1f1f1f';
          titleNode.style.fontSize = '14px';
          titleNode.style.fontWeight = '500';
          titleNode.style.lineHeight = '20px';

          dragImageNode.appendChild(iconNode);
          dragImageNode.appendChild(titleNode);
          // append 到 document.body，确保 setDragImage 能正确渲染（部分浏览器要求其在 DOM 中）。
          document.body.appendChild(dragImageNode);
          try {
            // 使用自定义节点作为拖拽预览图；偏移量用于对齐视觉中心。
            event.dataTransfer.setDragImage(dragImageNode, 16, 12);
          } finally {
            // setDragImage 之后移除临时节点。
            // requestAnimationFrame 可确保当前帧的拖拽预览渲染时序完成后再清理。
            window.requestAnimationFrame(() => {
              if (document.body.contains(dragImageNode)) {
                document.body.removeChild(dragImageNode);
              }
            });
          }

          handleDragStart?.({
            id: 'ai-analysis-entry',
            chartType: AI_ANALYSIS_TYPE,
            type: AI_ANALYSIS_TYPE,
            title: formatMessage({ id: 'dbl_ai_analysis_card_title' }),
            name: formatMessage({ id: 'dbl_ai_analysis_card_title' }),
            disable: isAiAnalysisDisabled,
            owner: '',
            createTime: '',
            img: '',
          });
        }}
        onDragEnd={() => {
          if (isAiAnalysisDisabled) {
            return;
          }

          handleDragStart?.(null);
        }}
      >
        <img src={StartsSvg} alt="" aria-hidden width={15} height={14} />
        <span className="dbl-ai-analysis-entry__title">
          {formatMessage({ id: 'dbl_ai_analysis_card_title' })}
        </span>
        <span className="dbl-ai-analysis-entry__badge">
          {formatMessage({ id: 'dbl_ai_analysis_entry_new' })}
        </span>
      </div>

      {
        hasChartPrivilege ? (<div className="dbl-charts-module-add" onClick={showCreateChartModel}>
          <SvgIcon type="add-bg-icon" className="dbl-charts-module-icon" />
          <Button type="link">{formatMessage({ id: 'dbl_myAnalysis_new' })}</Button>
        </div>) : ''
      }

      <div className="dbl-charts-module-search">
        <SearchBox
          className="dbl-search-search"
          value={searchValue}
          onChange={(value) => {
            onSearchChange(value);
          }}
          onClear={() => {
            onSearchChange('');
          }}
          placeholder={dblFormatMessage('search')}
        />
        {/* 和产品沟通后，产品要求中文翻译一样：Sort by Time，英文翻译为时间正序和时间倒序 */}
        <Bubble dark content={sort === 'desc' ? dblFormatMessage('sort_by_time_desc') : dblFormatMessage('sort_by_time_asc')} placement="top-end">
          <Button
            className="ns-btn--icon dbl-search-btn"
            onClick={() => {
              const sortVal = sort === 'desc' ? 'asc' : 'desc';
              onSortChange(sortVal);
            }}
          >
            <SvgIcon
              type={sort === 'desc' ? 'descending-order-icon' : 'ascending-order-icon'}
            />
          </Button>
        </Bubble>
      </div>
      <div className="dbl-charts-module-filter">
        <Checkbox
          value={onlyMyCharts}
          onChange={(checked) => {
            onOnlyMyChartsChange(checked);
          }}
        >
          {dblFormatMessage('show_only_my_charts')}
        </Checkbox>
      </div>
      <Abnormal
        isLoading={loading}
        isError={error}
        isNoData={chartList?.length === 0}
        nodataMsg={formatMessage({ id: 'dbl_common_no_data' })}
        component={(
          <>
            <RightChartList
              chartList={chartList}
              handleDragStart={handleDragStart}
              hasMore={hasMore}
              loadMore={loadMore}
            />
            {loadingMore && <Loading
              className="dbl-charts-module-loading-more"
              text={formatMessage({ id: 'common_loading' })}
            />}
          </>
        )}
      />
      <CreateChart visible={showCreateChart} close={() => setShowCreateChart(!showCreateChart)} handleOnSubmit={handleOnSubmit} />
    </div >
  );
};

export default RightChartTab;
